import { forwardRef, useCallback, useMemo, useState } from "react"
import {
  Box,
  Typography,
  Button,
  Tab,
  useTheme,
  Slide,
  SlideProps,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { BaseModal } from "../../modal"
import { getPostDetail, IPostDetail } from "utils/feature"
import { useTranslation } from "providers/i18n"
import { ScrollableTab } from "components/ScrollableTab"
import { useNavigate } from "react-router-dom"
import { Image } from "components/Image"
import { pushDataLayer } from "utils/googletagmanager"
import { GTM_POST_LOCATION, GTM_VIEW_POST } from "constant"
import { get } from "lodash"

const ModalTransition = forwardRef<HTMLElement, SlideProps>(function Transition(
  props,
  ref
) {
  return <Slide direction="up" ref={ref} {...props} />
})

export const SeeAndDoModal = ({
  data = [],
  sx = {},
  onClose,
  open = true,
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const {
    root,
    modalRoot,
    contentContainer,
    image,
    informationWrapper,
    textContentWrapper,
    titleText,
    descriptionText,
    buttonContainer,
    locationButton,
    locationWrapper,
    tabContainer,
    tabLabelContainer,
    locationNameText,
    levelText,
    tabItem,
    footerContentWrapper,
    headerContainer,
    subHeaderContainer,
    floorTextContainer,
    separator,
    floorText,
    venueText,
  } = theme.components?.[`SeeAndDoModal`].styleOverrides || {}
  const { scrollableTabProps = {} } =
    theme.components?.[`SeeAndDoModal`].defaultProps || {}

  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const transformedPosts = useMemo(() => data?.map(getPostDetail), [data])

  const activePost = useMemo(
    () => (transformedPosts[activeTabIndex] || {}) as IPostDetail,
    [transformedPosts, activeTabIndex]
  )

  const handleChange = useCallback(
    (event, newValue) => {
      setActiveTabIndex(newValue)
      pushDataLayer({
        event: GTM_VIEW_POST,
        post_type: get(transformedPosts[newValue], "type"),
        post_category: get(transformedPosts[newValue], "category.en"),
        post_id: get(transformedPosts[newValue], "id"),
        post_title: get(transformedPosts[newValue], "title.en"),
      })
    },
    [transformedPosts]
  )

  const handleClick = useCallback(() => {
    const locationUrl = get(activePost, "locationUrl")
    navigate(locationUrl)
    pushDataLayer({
      event: GTM_POST_LOCATION,
      post_id: get(activePost, "id"),
      post_location_name: get(activePost.location, "en"),
      post_type: get(activePost, "type"),
      post_category: get(activePost, "category.en"),
      post_title: get(activePost, "title.en"),
      post_location_url: locationUrl,
    })
  }, [navigate, activePost])

  const hasLocation = activePost?.hasLocation

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      sx={deepmerge(modalRoot, sx)}
      {...props}
      TransitionComponent={ModalTransition}
    >
      <Box sx={root}>
        <Box sx={contentContainer}>
          <Image src={activePost?.coverImage} sx={image} />
          <Box sx={informationWrapper}>
            <Box sx={headerContainer}>
              <Typography sx={titleText}>{t(activePost?.title)}</Typography>
              {hasLocation && (
                <Box sx={subHeaderContainer}>
                  <Box
                    sx={floorTextContainer}
                    className={activePost?.locationVenueRef}
                  >
                    <Typography sx={floorText}>
                      {t(activePost?.level)}
                    </Typography>
                  </Box>
                  <Typography sx={separator} component="span">
                    •
                  </Typography>
                  <Typography sx={venueText}>
                    {t(activePost?.locationVenueName)}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={textContentWrapper}>
              <Typography sx={descriptionText}>
                {t(activePost?.description)}
              </Typography>
            </Box>
            <Box sx={footerContentWrapper}>
              {hasLocation && (
                <Box sx={buttonContainer}>
                  <Button
                    variant="outlined"
                    sx={locationButton}
                    onClick={handleClick}
                  >
                    {t("View Location")}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={locationWrapper}>
          <ScrollableTab
            value={activeTabIndex}
            onChange={handleChange}
            sx={tabContainer}
            {...scrollableTabProps}
          >
            {transformedPosts.map((post, index) => {
              const { level = "" } = post?.locationDetail || {}
              return (
                <Tab
                  key={index}
                  label={
                    <Box sx={tabLabelContainer}>
                      <Typography
                        className="SeeAndDoModal-locationNameText"
                        sx={locationNameText}
                      >
                        {t(post?.title)}
                      </Typography>

                      {level && (
                        <Typography
                          variant="caption"
                          className="SeeAndDoModal-levelText"
                          sx={levelText}
                        >
                          {t(level)}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={tabItem}
                  value={index}
                />
              )
            })}
          </ScrollableTab>
        </Box>
      </Box>
    </BaseModal>
  )
}
